import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api';
import { knowledgeApi, type Finding, type FindingPage, type FunnelCase, type FunnelDetail, type Page, type Source, type SourceDetail, type SourceKind } from '../api/knowledge';
import { tasksApi, type TaskBusiness } from '../api/tasks';
import { Table, type Column } from '../components/Table';
import { t } from '../i18n';
import { Link } from '../router';
import { fmtWhen } from './tasks.format';
import './knowledge.css';

const FIRST = 10;
const MORE = 25;
const MAX = 100;
/** The sources block shows what came from the world; the agents' extracts have their own block (ADR-0011). */
const WORLD_KINDS: SourceKind[] = ['call_transcript', 'video', 'lesson'];

type Selected = { kind: 'finding' | 'funnel' | 'source'; id: string } | null;

function selectedFromUrl(): Selected {
  const p = new URLSearchParams(window.location.search);
  for (const kind of ['finding', 'funnel', 'source'] as const) {
    const id = p.get(kind);
    if (id) return { kind, id };
  }
  return null;
}

function errorText(e: unknown): string {
  const code = e instanceof ApiError ? e.code : e instanceof Error ? e.message : '';
  return code.endsWith('_not_found') ? t('knowledge.notFound') : code || t('common.error');
}

const isImage = (mime: string | undefined) => !!mime && mime.startsWith('image/') && mime !== 'image/heic';
const findingBadge = (s: Finding['status']) => (s === 'proposed' ? 'badge warn' : s === 'confirmed' ? 'badge ok' : 'badge');
const funnelBadge = (s: FunnelCase['status']) => (s === 'needs_human' ? 'badge warn' : s === 'complete' ? 'badge ok' : 'badge');
const sourceBadge = (s: Source['state']) => (s === 'awaiting_transcript' ? 'badge warn' : s === 'analyzed' ? 'badge ok' : 'badge');

/** A link that opens a row in the detail card instead of leaving the page. */
function Open({ to, onOpen, children }: { to: Selected & object; onOpen: (s: Selected) => void; children: React.ReactNode }) {
  return (
    <a
      href={`/knowledge?${to.kind}=${encodeURIComponent(to.id)}`}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        onOpen(to);
        window.scrollTo({ top: 0 });
      }}
    >
      {children}
    </a>
  );
}

/**
 * «Знання» (ADR-0010): one search over findings, funnel cases and sources, then the three blocks one
 * under the other, and a card for the one opened. The human decides only the uncertain findings.
 */
export function Knowledge({ canWrite }: { canWrite: boolean }) {
  const [businesses, setBusinesses] = useState<TaskBusiness[]>([]);
  const [businessId, setBusinessId] = useState('');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [limits, setLimits] = useState({ findings: FIRST, funnels: FIRST, sources: FIRST, extracts: FIRST });
  const [findings, setFindings] = useState<FindingPage | null>(null);
  const [funnels, setFunnels] = useState<Page<FunnelCase> | null>(null);
  const [sources, setSources] = useState<Page<Source> | null>(null);
  const [extracts, setExtracts] = useState<Page<Source> | null>(null);
  const [reload, setReload] = useState(0);
  const [selected, setSelected] = useState<Selected>(selectedFromUrl);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    tasksApi.businesses().then(setBusinesses).catch((e) => setError(errorText(e)));
  }, []);

  useEffect(() => {
    const h = setTimeout(() => setQ(typed.trim()), 300);
    return () => clearTimeout(h);
  }, [typed]);

  useEffect(() => {
    setLimits({ findings: FIRST, funnels: FIRST, sources: FIRST, extracts: FIRST });
  }, [q, businessId]);

  useEffect(() => {
    setError(null);
    const mine = ++seq.current;
    const base = { q: q || undefined, businessId: businessId || undefined, page: 1 };
    Promise.all([
      knowledgeApi.findings({ ...base, pageSize: limits.findings }),
      knowledgeApi.funnels({ ...base, pageSize: limits.funnels }),
      knowledgeApi.sources({ ...base, kind: WORLD_KINDS, pageSize: limits.sources }),
      knowledgeApi.sources({ ...base, kind: ['extract'], pageSize: limits.extracts }),
    ])
      .then(([f, fu, s, x]) => {
        if (mine !== seq.current) return;
        setFindings(f);
        setFunnels(fu);
        setSources(s);
        setExtracts(x);
      })
      .catch((e) => {
        if (mine === seq.current) setError(errorText(e));
      });
  }, [q, businessId, limits, reload]);

  useEffect(() => {
    window.history.replaceState({}, '', selected ? `/knowledge?${selected.kind}=${encodeURIComponent(selected.id)}` : '/knowledge');
  }, [selected]);

  async function decide(id: string, status: 'confirmed' | 'rejected') {
    setBusy(id);
    setError(null);
    try {
      await knowledgeApi.decide(id, status);
      setReload((n) => n + 1);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  }

  const decideButtons = (f: Finding) =>
    canWrite && f.status === 'proposed' ? (
      <span className="row k-decide">
        <button type="button" className="btn small" disabled={busy === f.id} onClick={() => void decide(f.id, 'confirmed')}>{t('knowledge.findings.confirm')}</button>
        <button type="button" className="btn secondary small" disabled={busy === f.id} onClick={() => void decide(f.id, 'rejected')}>{t('knowledge.findings.reject')}</button>
      </span>
    ) : null;

  const from = (f: Finding) => {
    if (f.source)
      return (
        <>
          <Open to={{ kind: 'source', id: f.source.id }} onOpen={setSelected}>{f.source.title}</Open>
          {f.sourceAt ? ` · ${f.sourceAt}` : ''}
        </>
      );
    if (f.funnelCase) return <Open to={{ kind: 'funnel', id: f.funnelCase.id }} onOpen={setSelected}>{f.funnelCase.title}</Open>;
    if (f.inboxItemId) return <Link to={`/inbox?id=${encodeURIComponent(f.inboxItemId)}`}>{t('knowledge.findings.fromInbox')}</Link>;
    if (f.sourceUrl) return <a href={f.sourceUrl} target="_blank" rel="noreferrer">{t('knowledge.findings.fromLink')}</a>;
    return <span className="muted">—</span>;
  };

  const findingColumns = useMemo<Column<Finding>[]>(
    () => [
      {
        key: 'what',
        title: t('knowledge.findings.col.what'),
        wrap: true,
        render: (f) => (
          <>
            <span className="badge">{t(`knowledge.kind.${f.kind}`)}</span>{' '}
            <Open to={{ kind: 'finding', id: f.id }} onOpen={setSelected}>«{f.text}»</Open>
            {f.context && (
              <>
                <br />
                <span className="muted">{f.context}</span>
              </>
            )}
          </>
        ),
      },
      { key: 'segment', title: t('knowledge.findings.col.segment'), wrap: true, sortValue: (f) => f.segment ?? '', render: (f) => f.segment ?? <span className="muted">—</span> },
      { key: 'business', title: t('knowledge.findings.col.business'), render: (f) => f.business?.name ?? <span className="muted">—</span> },
      { key: 'from', title: t('knowledge.findings.col.from'), wrap: true, render: from },
      {
        key: 'status',
        title: t('knowledge.findings.col.status'),
        render: (f) => (
          <>
            <span className={findingBadge(f.status)}>{t(`knowledge.status.${f.status}`)}</span>
            {decideButtons(f)}
          </>
        ),
      },
      { key: 'when', title: t('knowledge.findings.col.when'), sortValue: (f) => f.createdAt, render: (f) => <span className="muted">{fmtWhen(f.createdAt)}</span> },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, busy],
  );

  const funnelColumns = useMemo<Column<FunnelCase>[]>(
    () => [
      {
        key: 'funnel',
        title: t('knowledge.funnels.col.funnel'),
        wrap: true,
        sortValue: (f) => f.title,
        render: (f) => (
          <>
            <Open to={{ kind: 'funnel', id: f.id }} onOpen={setSelected}>{f.title}</Open>
            {f.niche && (
              <>
                <br />
                <span className="muted">{f.niche}</span>
              </>
            )}
          </>
        ),
      },
      { key: 'offer', title: t('knowledge.funnels.col.offer'), wrap: true, render: (f) => [f.offer, f.price].filter(Boolean).join(' · ') || <span className="muted">—</span> },
      { key: 'next', title: t('knowledge.funnels.col.next'), render: (f) => f.nextChannel ?? <span className="muted">—</span> },
      { key: 'steps', title: t('knowledge.funnels.col.steps'), sortValue: (f) => f.stepCount, render: (f) => <span className="num">{f.stepCount}</span> },
      { key: 'status', title: t('knowledge.funnels.col.status'), render: (f) => <span className={funnelBadge(f.status)}>{t(`knowledge.funnel.${f.status}`)}</span> },
      { key: 'updated', title: t('knowledge.funnels.col.updated'), sortValue: (f) => f.updatedAt, render: (f) => <span className="muted">{fmtWhen(f.updatedAt)}</span> },
    ],
    [],
  );

  const sourceColumns = useMemo<Column<Source>[]>(
    () => [
      {
        key: 'source',
        title: t('knowledge.sources.col.source'),
        wrap: true,
        sortValue: (s) => s.title,
        render: (s) => (
          <>
            <Open to={{ kind: 'source', id: s.id }} onOpen={setSelected}>{s.title}</Open>
            <br />
            <span className="muted">{t(`knowledge.sourceKind.${s.kind}`)}</span>
          </>
        ),
      },
      { key: 'business', title: t('knowledge.sources.col.business'), render: (s) => s.business?.name ?? <span className="muted">—</span> },
      { key: 'state', title: t('knowledge.sources.col.state'), render: (s) => <span className={sourceBadge(s.state)}>{t(`knowledge.state.${s.state}`)}</span> },
      { key: 'findings', title: t('knowledge.sources.col.findings'), sortValue: (s) => s.findingCount, render: (s) => <span className="num">{s.findingCount}</span> },
      { key: 'when', title: t('knowledge.sources.col.when'), sortValue: (s) => s.createdAt, render: (s) => <span className="muted">{fmtWhen(s.createdAt)}</span> },
    ],
    [],
  );

  const extractColumns = useMemo<Column<Source>[]>(
    () => [
      { key: 'extract', title: t('knowledge.extracts.col.extract'), wrap: true, sortValue: (s) => s.title, render: (s) => <Open to={{ kind: 'source', id: s.id }} onOpen={setSelected}>{s.title}</Open> },
      { key: 'tags', title: t('knowledge.extracts.col.tags'), wrap: true, render: (s) => (s.tags.length ? s.tags.join(', ') : <span className="muted">—</span>) },
      { key: 'when', title: t('knowledge.sources.col.when'), sortValue: (s) => s.createdAt, render: (s) => <span className="muted">{fmtWhen(s.createdAt)}</span> },
    ],
    [],
  );

  const more = (key: keyof typeof limits, page: Page<unknown> | null) =>
    page && page.total > page.rows.length && limits[key] < MAX ? (
      <div className="row" style={{ marginTop: 'var(--sp-2)', justifyContent: 'space-between' }}>
        <span className="muted">{t('knowledge.shown', { shown: page.rows.length, total: page.total })}</span>
        <button type="button" className="btn secondary small" onClick={() => setLimits((l) => ({ ...l, [key]: Math.min(MAX, l[key] + MORE) }))}>{t('knowledge.more')}</button>
      </div>
    ) : null;

  return (
    <div className="knowledge">
      <h1>{t('knowledge.title')}</h1>
      <p className="muted">{t('knowledge.intro')}</p>
      <div className="row filters" style={{ margin: 'var(--sp-3) 0' }}>
        <input className="input k-search" type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t('knowledge.search')} aria-label={t('knowledge.search')} />
        <select value={businessId} onChange={(e) => setBusinessId(e.target.value)} aria-label={t('knowledge.allBusinesses')}>
          <option value="">{t('knowledge.allBusinesses')}</option>
          {businesses.map((b) => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </div>
      {error && <div className="error" style={{ marginBottom: 'var(--sp-3)' }}>{error}</div>}

      {selected && <Detail key={`${selected.kind}-${selected.id}-${reload}`} selected={selected} onOpen={setSelected} onError={(e) => setError(errorText(e))} decideButtons={decideButtons} from={from} />}

      <h2>
        {t('knowledge.findings.title')} {findings && <span className="muted">{t('knowledge.findings.counts', { proposed: findings.counts.proposed, confirmed: findings.counts.confirmed })}</span>}
      </h2>
      <Table columns={findingColumns} rows={findings?.rows ?? []} rowKey={(f) => f.id} empty={findings === null ? t('common.loading') : t('knowledge.findings.empty')} />
      {more('findings', findings)}

      <h2 className="k-section">{t('knowledge.funnels.title')}</h2>
      <Table columns={funnelColumns} rows={funnels?.rows ?? []} rowKey={(f) => f.id} empty={funnels === null ? t('common.loading') : t('knowledge.funnels.empty')} />
      {more('funnels', funnels)}

      <h2 className="k-section">{t('knowledge.sources.title')}</h2>
      <Table columns={sourceColumns} rows={sources?.rows ?? []} rowKey={(s) => s.id} empty={sources === null ? t('common.loading') : t('knowledge.sources.empty')} />
      {more('sources', sources)}

      <h2 className="k-section">{t('knowledge.extracts.title')}</h2>
      <p className="muted">{t('knowledge.extracts.hint')}</p>
      <Table columns={extractColumns} rows={extracts?.rows ?? []} rowKey={(s) => s.id} empty={extracts === null ? t('common.loading') : t('knowledge.extracts.empty')} />
      {more('extracts', extracts)}
    </div>
  );
}

function Detail({ selected, onOpen, onError, decideButtons, from }: { selected: Selected & object; onOpen: (s: Selected) => void; onError: (e: unknown) => void; decideButtons: (f: Finding) => React.ReactNode; from: (f: Finding) => React.ReactNode }) {
  const [finding, setFinding] = useState<Finding | null>(null);
  const [funnel, setFunnel] = useState<FunnelDetail | null>(null);
  const [source, setSource] = useState<SourceDetail | null>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (selected.kind === 'finding') knowledgeApi.finding(selected.id).then(setFinding).catch(onError);
    if (selected.kind === 'funnel') knowledgeApi.funnel(selected.id).then(setFunnel).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.kind, selected.id]);

  useEffect(() => {
    if (selected.kind === 'source') knowledgeApi.source(selected.id, offset).then(setSource).catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.kind, selected.id, offset]);

  const header = (title: string, meta: React.ReactNode) => (
    <>
      <div className="row between">
        <h2 style={{ margin: 0, overflowWrap: 'anywhere' }}>{title}</h2>
        <button type="button" className="btn secondary small" onClick={() => onOpen(null)}>{t('knowledge.close')}</button>
      </div>
      <p className="muted">{meta}</p>
    </>
  );

  const findingList = (rows: Finding[]) => (
    <ul className="k-findings">
      {rows.map((f) => (
        <li key={f.id}>
          <span className="badge">{t(`knowledge.kind.${f.kind}`)}</span> <span className="k-quote">«{f.text}»</span> <span className={findingBadge(f.status)}>{t(`knowledge.status.${f.status}`)}</span>
          {f.sourceAt && <span className="muted"> · {f.sourceAt}</span>}
          {f.segment && <span className="muted"> · {f.segment}</span>}
        </li>
      ))}
    </ul>
  );

  if (selected.kind === 'finding' && finding)
    return (
      <section className="card k-detail" aria-label={finding.text}>
        {header(`«${finding.text}»`, <>{t(`knowledge.kind.${finding.kind}`)}{finding.business ? ` · ${finding.business.name}` : ''}{finding.segment ? ` · ${finding.segment}` : ''} · {fmtWhen(finding.createdAt)} · <span className={findingBadge(finding.status)}>{t(`knowledge.status.${finding.status}`)}</span></>)}
        {finding.context && <p>{finding.context}</p>}
        <p>{t('knowledge.findings.col.from')}: {from(finding)}</p>
        {finding.tags.length > 0 && <p className="muted">#{finding.tags.join(' #')}</p>}
        {decideButtons(finding)}
      </section>
    );

  if (selected.kind === 'funnel' && funnel) {
    const facts: Array<[string, string | null]> = [
      [t('knowledge.funnel.niche'), funnel.niche],
      [t('knowledge.funnel.product'), funnel.product],
      [t('knowledge.funnel.owner'), funnel.owner],
      [t('knowledge.funnel.entry'), [funnel.entryPlatform, funnel.entryText].filter(Boolean).join(' — ') || null],
      [t('knowledge.funnel.offer'), funnel.offer],
      [t('knowledge.funnel.price'), funnel.price],
      [t('knowledge.funnel.next'), funnel.nextChannel],
      [t('knowledge.funnel.design'), funnel.design],
    ];
    return (
      <section className="card k-detail" aria-label={funnel.title}>
        {header(
          funnel.title,
          <>
            {t('knowledge.funnel.kind')}
            {funnel.business ? ` · ${funnel.business.name}` : ''} · <span className={funnelBadge(funnel.status)}>{t(`knowledge.funnel.${funnel.status}`)}</span> · {fmtWhen(funnel.updatedAt)}
            {funnel.inboxItemId && (
              <>
                {' · '}
                <Link to={`/inbox?id=${encodeURIComponent(funnel.inboxItemId)}`}>{t('knowledge.funnel.fromInbox')}</Link>
              </>
            )}
          </>,
        )}
        {funnel.note && <p className="k-note">{funnel.note}</p>}
        <div className="k-facts">
          {facts
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <div key={k}>
                <span>{k}</span>
                {v}
              </div>
            ))}
        </div>
        {funnel.entryFile && isImage(funnel.entryFile.mimeType) && !funnel.steps.some((st) => st.file?.id === funnel.entryFile!.id) && (
          <a href={funnel.entryFile.path} target="_blank" rel="noreferrer">
            <img className="k-entry" src={funnel.entryFile.path} alt={funnel.title} />
          </a>
        )}
        <h3>{t('knowledge.funnel.steps', { n: funnel.steps.length })}</h3>
        {funnel.steps.length === 0 && <p className="muted">{t('knowledge.funnel.noSteps')}</p>}
        {funnel.steps.map((s) => (
          <div className="k-step" key={s.id}>
            <span className="n">{s.order}</span>
            {s.file && isImage(s.file.mimeType) && (
              <a href={s.file.path} target="_blank" rel="noreferrer">
                <img className="k-thumb" src={s.file.path} alt="" loading="lazy" />
              </a>
            )}
            <div>
              <b>{s.seen}</b> {s.cta && <span className="badge">{s.cta}</span>}
              {s.text && <p className="k-quote">«{s.text}»</p>}
              {(s.leadsTo || s.url) && (
                <span className="muted">
                  {s.leadsTo ? `→ ${s.leadsTo}` : ''} {s.url && <a href={s.url} target="_blank" rel="noreferrer">{t('knowledge.funnel.open')}</a>}
                </span>
              )}
            </div>
          </div>
        ))}
        {funnel.findings.length > 0 && (
          <>
            <h3>{t('knowledge.funnel.findings', { n: funnel.findings.length })}</h3>
            {findingList(funnel.findings)}
          </>
        )}
        {funnel.url && (
          <p style={{ overflowWrap: 'anywhere' }}>
            <a href={funnel.url} target="_blank" rel="noreferrer">{t('knowledge.funnel.open')}</a> <span className="muted">{funnel.url}</span>
          </p>
        )}
      </section>
    );
  }

  if (selected.kind === 'source' && source)
    return (
      <section className="card k-detail" aria-label={source.title}>
        {header(source.title, <>{t(`knowledge.sourceKind.${source.kind}`)}{source.business ? ` · ${source.business.name}` : ''} · {fmtWhen(source.createdAt)} · <span className={sourceBadge(source.state)}>{t(`knowledge.state.${source.state}`)}</span></>)}
        {source.url && (
          <p style={{ overflowWrap: 'anywhere' }}>
            <a href={source.url} target="_blank" rel="noreferrer">{t('knowledge.source.open')}</a> <span className="muted">{source.url}</span>
          </p>
        )}
        {source.findings.length > 0 && (
          <>
            <h3>{t('knowledge.source.findings', { n: source.findings.length })}</h3>
            {findingList(source.findings)}
          </>
        )}
        <h3>{t('knowledge.source.text')}</h3>
        {!source.text && <p className="muted">{t('knowledge.source.noText')}</p>}
        {source.text && (
          <>
            <pre className="codeblock k-text">{source.text.text}</pre>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">{t('knowledge.source.slice', { from: source.text.offset, to: source.text.offset + source.text.text.length, length: source.text.length })}</span>
              {source.text.nextOffset !== null && (
                <button type="button" className="btn secondary small" onClick={() => setOffset(source.text!.nextOffset!)}>{t('knowledge.source.nextSlice')}</button>
              )}
            </div>
          </>
        )}
      </section>
    );

  return null;
}
